<template>
  <div>
    <trek-loading top :is-loading="loading">
      <Callout v-if="errorMessage" type="alert" icon="warning">{{ errorMessage }}</Callout>

      <Callout v-if="discontinuedMessage" type="warning" icon="info_outline" :is-dismissible="true">{{
        discontinuedMessage
      }}</Callout>

      <section class="savedList__meta">
        <!-- Delete list prompt -->
        <el-dialog
          :visible.sync="dialogVisible"
          :title="getBaseProperty('confirmDelete')"
          :center="true"
          :show-close="false"
          :close-on-press-escape="false"
          :close-on-click-modal="false"
          :z-index="16000011"
          :close-dialog-tooltip="bp('closeDialogToolTip')"
          qaid="saved-lists.delete.list.modal"
        >
          <template slot="footer">
            <div class="buttons buttons--center">
              <trek-button alert qaid="saved-lists.delete.list.yes" @click="confirmDelete">{{
                getBaseProperty('yesDelete')
              }}</trek-button>
              <trek-button secondary qaid="saved-lists.delete.list.no" @click="dialogVisible = false">{{
                getBaseProperty('save')
              }}</trek-button>
            </div>
          </template>
        </el-dialog>

        <!-- Delete selected entries prompt -->
        <el-dialog
          :visible.sync="dialogVisibleRemove"
          :title="getBaseProperty('confirmDeleteListItem')"
          :center="true"
          :show-close="false"
          :close-on-press-escape="false"
          :close-on-click-modal="false"
          :z-index="16000011"
          :close-dialog-tooltip="bp('closeDialogToolTip')"
          qaid="saved-lists.item.delete.list.modal"
        >
          <template slot="footer">
            <div class="buttons buttons--center">
              <trek-button alert qaid="saved-lists.item.delete.list.yes" @click="confirmRemove">{{
                getBaseProperty('yesDelete')
              }}</trek-button>
              <trek-button secondary qaid="saved-lists.item.delete.list.no" @click="dialogVisibleRemove = false">{{
                getBaseProperty('save')
              }}</trek-button>
            </div>
          </template>
        </el-dialog>

        <p v-show="selectedList.bikesEntries.length || selectedList.partsEntries.length" class="mb-3 text-base">
          {{ getBaseProperty('notAllocated') }}
        </p>

        <div
          v-if="!(userLists.length || accountLists.length)"
          id="savedListDisclaimer--noSavedLists"
          class="bg-gray-05 p-3"
        >
          <span>{{ getBaseProperty('noSavedLists') }}</span>
        </div>

        <div v-if="userLists.length || accountLists.length">
          <!--Select a Saved List-->
          <saved-list-dropdown @list-changed="clearMinQtyErrorsAndChangeList"></saved-list-dropdown>
          <!--Current Saved List Info-->
          <saved-list-meta
            v-if="selectedList.id"
            :list="selectedList"
            :cancel-list-name="bp('cancelListName')"
            :created-by-translation="getBaseProperty('createdBy')"
          ></saved-list-meta>
        </div>
      </section>

      <!-- Tabs (SKU and Apple Labels) -->
      <tabs ref="tabs" :active-name="selectedList.bikesEntries.length ? 'bikes' : 'accessories'">
        <!-- Bikes Tab -->
        <tab-pane v-if="selectedList.bikesEntries.length" name="bikes">
          <div slot="label">
            <h3 class="heading heading--md m-0">
              {{ getBaseProperty('bikes') }} ({{ !selectedList.bikesEntries ? 0 : selectedList.bikesEntries.length }})
            </h3>
          </div>

          <saved-list-grid
            :key="changeCounter"
            :base-properties="baseProperties"
            :disable-interactions="disableInteractions"
            :disable-actions="disableActions"
            :list="selectedList.bikesEntries"
            :min-qty-move-failed-list="minQtyFailList"
            entries-name="bikesEntries"
            @update-sort-type="updateSortList"
            @grid-move-to-cart="moveToCart"
            @grid-remove="onRemove"
            @grid-delete="onDelete"
          >
            <template v-slot:summary>
              <saved-list-summary :list="selectedList" :base-properties="baseProperties" />
            </template>
          </saved-list-grid>
        </tab-pane>

        <!-- Parts/Accessories Tab -->
        <tab-pane v-if="selectedList.partsEntries.length" name="accessories">
          <div slot="label">
            <h3 class="heading heading--md m-0">
              {{ getBaseProperty('accessories') }} ({{
                !selectedList.partsEntries ? 0 : selectedList.partsEntries.length
              }})
            </h3>
          </div>

          <saved-list-grid
            :key="changeCounter"
            :base-properties="baseProperties"
            :disable-interactions="disableInteractions"
            :disable-actions="disableActions"
            :list="selectedList.partsEntries"
            :min-qty-move-failed-list="minQtyFailList"
            entries-name="partsEntries"
            @update-sort-type="updateSortList"
            @grid-move-to-cart="moveToCart"
            @grid-remove="onRemove"
            @grid-delete="onDelete"
          >
            <template v-slot:summary>
              <saved-list-summary :list="selectedList" :base-properties="baseProperties" />
            </template>
          </saved-list-grid>
        </tab-pane>
      </tabs>

      <!-- Needed for scenario where there's a list with no entries, in case user wants to delete it -->
      <div
        v-if="
          (userLists.length || accountLists.length) &&
            !selectedList.partsEntries.length &&
            !selectedList.bikesEntries.length
        "
        class="w-full lg:w-auto lg:flex-grow"
      >
        <div class="buttons buttons--right-for-md">
          <trek-button
            v-if="!selectedList.readOnly && !disableInteractions"
            tertiary
            qaid="saved-lists.button-delete-secondary"
            :disabled="disableInteractions"
            @click="onDelete"
            >{{ getBaseProperty('delete') }}</trek-button
          >
        </div>
      </div>
    </trek-loading>

    <el-dialog
      :visible.sync="dialogVisibleOtherVendor"
      :center="true"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      :z-index="16000011"
      :close-dialog-tooltip="bp('closeDialogToolTip')"
      @close="dialogVisibleOtherVendor = false"
    >
      <template slot="title">
        <span class="el-dialog__title heading heading--md" qaid="saved-lists.otherVendorTitle">{{
          bp('unableMoveToCart')
        }}</span>
      </template>
      <div>
        <p class="text-left mb-3" qaid="saved-lists.otherVendorDescription">{{ bp('notSoldByVendor') }}</p>

        <div class="b2b-scroll-list-container w-full">
          <div class="b2b-scroll-list">
            <table class="b2b-grid">
              <thead class="b2b-grid__header">
                <tr>
                  <th class="b2b-grid__cell b2b-grid__head" qaid="saved-lists.otherVendorSkuHeading">
                    {{ bp('sku') }}
                  </th>
                  <th class="b2b-grid__cell b2b-grid__head" qaid="saved-lists.otherVendorNameHeading">
                    {{ bp('description') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in otherVendorItems" :key="index">
                  <td class="b2b-grid__cell table-cell" :qaid="`saved-lists.otherVendorSkuRow-${index}`">
                    {{ item.sku }}
                  </td>
                  <td class="b2b-grid__cell table-cell" :qaid="`saved-lists.otherVendorNameRow-${index}`">
                    {{ item.itemName }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <template slot="footer">
        <div class="buttons buttons--center">
          <trek-button primary qaid="saved-lists.otherVendorBackButton" @click="dialogVisibleOtherVendor = false"
            ><span v-html="bp('back')"></span
          ></trek-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import {axiosToast, getToast} from '../../utils/axios';
import {mapState, mapActions, mapMutations} from 'vuex';
import Vue from 'vue';
import get from 'lodash/get';
import find from 'lodash/find';
import first from 'lodash/first';
import Tabs from '@/components/tabs/Tabs';
import Callout from '@/components/Callout';
import ElDialog from '@/components/Dialog.vue';
import TabPane from '@/components/tabs/TabPane';
import TrekButton from '@/components/TrekButton';
import TrekLoading from '@/components/TrekLoading';
import SavedListMeta from '@/components/saved-lists/SavedListMeta';
import SavedListGrid from '@/components/saved-lists/SavedListGrid';
import SavedListSummary from '@/components/saved-lists/SavedListSummary';
import SavedListDropdown from '@/components/saved-lists/SavedListDropdown';

export default {
  components: {
    Tabs,
    TabPane,
    Callout,
    ElDialog,
    TrekButton,
    TrekLoading,
    SavedListMeta,
    SavedListGrid,
    SavedListSummary,
    SavedListDropdown,
  },
  props: {
    baseProperties: {
      type: Array,
      default() {
        return [];
      },
    },
  },

  data() {
    return {
      dialogVisible: false,
      dialogVisibleRemove: false,
      dialogVisibleOtherVendor: false,
      loading: true,
      initDone: false,
      lists: [],
      otherVendorItems: [],
      minQtyFailList: {},
      changeCounter: 0,
    };
  },

  computed: {
    disableInteractions() {
      return this.selectedList.readOnly;
    },

    disableActions() {
      return (
        this.selectedList.readOnly ||
        (!this.selectedList.bikesEntries.find((line) => line.checked) &&
          !this.selectedList.partsEntries.find((line) => line.checked))
      );
    },

    ...mapState('savedLists', ['userLists', 'accountLists', 'selectedList', 'errorMessage', 'discontinuedMessage']),
    ...mapState('backend', ['isB2BPurchasable']),
  },

  watch: {
    accountLists() {
      this.onListsChanged();
    },
    userLists() {
      this.onListsChanged();
    },
  },

  created() {
    this.fetchAllLists();
  },

  methods: {
    onListsChanged() {
      if (!this.initDone) {
        // Init lists when data has been loaded
        const selectedList = [...this.userLists, ...this.accountLists][0];

        if (selectedList) {
          let id = selectedList.id;
          let sortKey = 'savedlistdateadded';
          this.fetchListSort({id, sortKey});
        }
        this.loading = false;
        this.initDone = true;
      }
    },

    getBaseProperty(name) {
      const baseProperty = find(this.baseProperties, (baseProperty) => baseProperty.name === name);
      return baseProperty ? baseProperty.label : '';
    },

    updateSortList(sortKey) {
      let id = this.selectedList.id;
      this.fetchListSort({id, sortKey});
    },

    changeList(id) {
      if (id) {
        this.loading = true;
        this.fetchList(id).finally(() => (this.loading = false));
      } else {
        this.clearSelectedList();
      }
    },

    clearMinQtyErrorsAndChangeList(id) {
      // this counter var is used as key to trick Vue to trigger a form refresh to remove form
      // errors from previous list since forceUpdate is not working as expected
      this.changeCounter++;
      this.minQtyFailList = {};
      this.changeList(id);
    },

    getCheckedEntries() {
      const {bikesEntries, partsEntries} = this.selectedList;
      const entries = [...bikesEntries, ...partsEntries].reduce((acc, entry) => {
        if (entry.checked) {
          acc.push({
            sku: entry.sku,
            qty: parseInt(entry.orderedQty, 10),
            checked: true,
            lineNote: entry.lineNote && entry.lineNote.length > 0 ? entry.lineNote : null,
          });
        }

        return acc;
      }, []);

      return entries;
    },

    onDelete() {
      this.dialogVisible = true;
    },

    onRemove() {
      this.dialogVisibleRemove = true;
    },

    closeDialog() {
      this.dialogVisible = false;
    },

    closeDialogRemove() {
      this.dialogVisibleRemove = false;
    },

    confirmDelete() {
      this.loading = true;
      this.setUseToastInterceptor(true);
      this.deleteList(this.selectedList.id)
        .then(() => {
          const newSelected = first([...this.userLists, ...this.accountLists]);
          const newId = newSelected ? newSelected.id : null;

          this.changeList(newId);
        })
        .finally(() => {
          this.dialogVisible = false;
          this.loading = false;
        });
    },

    undoMoveToCart(id, entries) {
      this.loading = true;

      this.setUseToastInterceptor(false);
      axios
        .delete(`/cart/add/${id}`, {data: {entries}})
        .then(() => {
          return this.updateSelectedEntries({id, entries}).then(() => {
            this.setUseToastInterceptor(true);
            this.changeList(id);
            this.fetchMiniCart();
          });
        })
        .finally(() => (this.loading = false));
    },

    moveToCart() {
      const entries = this.getCheckedEntries();
      const {id} = this.selectedList;

      this.setUseToastInterceptor(false);
      this.minQtyFailList = {};
      axios
        .put(`/cart/add/${id}`, {entries})
        .then(({data}) => {
          let failedSkus = (get(data, 'data.failedSKUs') || []).map((item) => item.sku);
          let failedMessages = (get(data, 'data.failedSKUs') || []).map((item) => item.message.basePropertyValue);

          for (let i = 0; i < failedSkus.length; i++) {
            Vue.set(this.minQtyFailList, failedSkus[i], failedMessages[i]);
          }

          const {type, message, toastDuration} = getToast(data.meta.feedback);
          if (type === 'alert') {
            const messageKey = get(data, 'meta.feedback.message.basePropertyKey');
            if (messageKey === 'distributor.B2B.advancedOrder.error.cannotAdd') {
              const items = get(data, 'data.cartEntries');
              if (items && items.length) {
                let nonAOItems = [];
                items.forEach((item) => {
                  nonAOItems.push({sku: item.sku, itemName: item.itemName});
                });
                this.setDisplayAddErrorModal(true);
                this.setNonAOItems(nonAOItems);
              }
            } else if (messageKey === 'distributor.B2B.notSoldByVendor') {
              const items = get(data, 'data.cartEntries');
              if (items && items.length) {
                this.otherVendorItems = [];
                items.forEach((item) => {
                  this.otherVendorItems.push({sku: item.sku, itemName: item.itemName});
                });
                this.dialogVisibleOtherVendor = true;
              }
            } else {
              if (!(failedSkus && failedSkus.length)) {
                this.$notify({
                  type,
                  message,
                  duration: toastDuration,
                  showClose: true,
                  link: data.meta.feedback.link,
                });
              }
            }
          } else {
            const {id} = data.data;
            this.$notify({
              type,
              message,
              actionClickText: this.getBaseProperty('undo'),
              actionClick: () => this.undoMoveToCart(id, entries),
            });
          }

          this.setUseToastInterceptor(true);
        })
        .catch(({response}) => {
          axiosToast(response);
        })
        .finally(() => {
          this.changeList(this.selectedList.id);
          this.fetchMiniCart();
          this.setUseToastInterceptor(true);
          this.$refs.tabs.setCurrentName(this.selectedList.bikesEntries.length ? 'bikes' : 'accessories');
          this.loading = false;
        });
    },

    confirmRemove() {
      this.loading = true;

      const entries = this.getCheckedEntries().map((entry) => {
        entry.qty = 0;

        return entry;
      });

      this.removeSelectedEntries(entries)
        .then(() => {
          this.dialogVisibleRemove = false;
          this.changeList(this.selectedList.id);
          this.$refs.tabs.setCurrentName(this.selectedList.bikesEntries.length ? 'bikes' : 'accessories');
        })
        .finally(() => (this.loading = false));
    },

    ...mapMutations('global', ['setUseToastInterceptor']),
    ...mapMutations('advancedOrdering', ['setNonAOItems', 'setDisplayAddErrorModal']),
    ...mapActions('savedLists', [
      'fetchAllLists',
      'fetchList',
      'fetchListSort',
      'deleteList',
      'removeSelectedEntries',
      'updateSelectedEntries',
      'clearSelectedList',
    ]),
    ...mapActions('miniCart', ['fetchMiniCart']),
  },
};
</script>
